import { useCallback, useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Select, Button, Icon, Modal, Dropdown, Menu, Loader, Form } from "semantic-ui-react";
import ReactQuill from "react-quill";
import { IChapter, IChapterContent, ITableChapter } from "../../models/chapter";
import { IParagraph } from "../../models/paragraph";
import { ReportPublishControls } from "../Report/ReportPublishControls";
import {
    getChaptersForReport,
    getChapterContent,
    reorderContent,
} from "../../services/chaptersService";
import { FigureSelectionModal } from "./FigureSelectionModal";
import {
    createParagraph,
    updateParagraph,
    deleteParagraph,
} from "../../services/paragraphsService";
import {
    addFigureToChapter,
    removeFigureFromChapter,
    updateFigureChapter,
} from "../../services/figureChaptersService";
import { toast } from "../..";
import toolbarConfig from "./TextEditorToolbarConfig.json";
import { IFigureChapter } from "../../models/chapter";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import "react-quill/dist/quill.snow.css";
import "./Chapter.css";
import { TableSelectionModal } from "./TableSelectionModal";
import {
    addTableToChapter,
    removeTableFromChapter,
    updateTableChapter,
} from "../../services/tableChaptersService";
import { TableComponent } from "../FiguresAndTables/Tables/TableComponent";
import { getTablesData } from "../../services/tablesService";
import { IReport } from "../../models/report";
import { getReport } from "../../services/reportsService";

export const Chapter = () => {
    const history = useHistory();
    const params: { reportId: string; chapterId: string } = useParams();

    const [chapters, setChapters] = useState<IChapter[]>([]);

    const [paragraphDeleteSelection, setParagraphDeleteSelection] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [figureSelectDialogOpen, setFigureSelectDialogOpen] = useState(false);
    const [tableSelectDialogOpen, setTableSelectDialogOpen] = useState(false);

    const [deleteSelection, setDeleteSelection] = useState("");
    const [deleteOpen, setDeleteOpen] = useState(false);

    const [editNameDialogOpen, setEditNameDialogOpen] = useState(false);
    const [editNameSelection, setEditNameSelection] = useState("");
    const [name, setName] = useState("");
    const [entity, setEntity] = useState<"figure" | "table" | "">("");

    const [chapterContent, setChapterContent] = useState<IChapterContent[]>([]);
    const [loadingContent, setLoadingContent] = useState(true);
    const [contentFocused, setContentFocused] = useState("");

    const [tablesData, setTablesData] = useState<Record<string, string[][]>>({});

    const [report, setReport] = useState<Partial<IReport>>({});
    useEffect(() => {
        if (!params.reportId) {
            return;
        }
        getReport(params.reportId).then(res => {
            setReport(res);
        });
    }, [params.reportId]);

    const refreshContent = useCallback(async () => {
        if (params.chapterId) {
            try {
                setLoadingContent(true);
                let content = await getChapterContent(params.chapterId);
                setChapterContent(content);
            } catch (err) {
                toast("A problem occured while fetching chapter content", false, 1500);
            } finally {
                setLoadingContent(false);
            }
        }
    }, [params.chapterId]);

    useEffect(() => {
        setLoadingContent(true);
        if (chapterContent.length === 0) {
            return;
        }
        // get only the table chapters
        let tableGuids = chapterContent.map(cc => {
            if ((cc as ITableChapter).tableId) {
                return (cc as ITableChapter).tableId;
            }
            return "";
        });
        // remove empty guids
        tableGuids = tableGuids.filter(tg => tg !== "");
        getTablesData(tableGuids).then(res => {
            setTablesData(res);
        });
        setLoadingContent(false);
    }, [chapterContent]);

    useEffect(() => {
        getChaptersForReport(params.reportId).then(res => {
            setChapters(res);
        });
    }, [params.reportId]);

    useEffect(() => {
        refreshContent();
    }, [params.chapterId, refreshContent]);

    const saveNewParagraph = () => {
        let paragraph: Partial<IParagraph> = {
            chapterId: params.chapterId,
        };

        createParagraph(paragraph).then(res => {
            toast("Succesfully created a new paragraph.", true, 1500);
            refreshContent();
        });
    };

    const saveExistingParagraph = (paragraphId: string) => {
        let paragraph = chapterContent.find(p => p.id === paragraphId) as IParagraph;

        if (paragraph) {
            updateParagraph(paragraph.id, paragraph);
        }
    };

    const onDeleteParagraph = () => {
        if (paragraphDeleteSelection) {
            deleteParagraph(paragraphDeleteSelection).then(res => {
                if (res.ok) {
                    toast("Paragraph was successfully deleted.", true, 1500);
                } else {
                    toast("There was a problem with deleting the paragraph.", false, 1500);
                }

                refreshContent();
            });

            setDeleteDialogOpen(false);
        }
    };

    const onDelete = () => {
        if (!deleteSelection) {
            return;
        }

        const removeFunction =
            entity === "figure"
                ? removeFigureFromChapter(deleteSelection)
                : removeTableFromChapter(deleteSelection);

        removeFunction.then(res => {
            if (res.ok) {
                toast(`${entity} was successfully removed form chapter.`, true, 1500);
            } else {
                toast(`There was a problem with removing ${entity} from chapter.`, false, 1500);
            }

            refreshContent();
        });

        setDeleteOpen(false);
    };

    const handleAddFigure = (guid: string, figureName: string) => {
        let newFigureChapter: Partial<IFigureChapter> = {
            chapterId: params.chapterId,
            figureId: guid,
            name: figureName,
        };

        addFigureToChapter(newFigureChapter).then(_res => {
            refreshContent();
            setFigureSelectDialogOpen(false);
        });
    };

    const handleAddTable = (guid: string, tableName: string) => {
        let newTableChapter: Partial<ITableChapter> = {
            chapterId: params.chapterId,
            tableId: guid,
            name: tableName,
        };

        addTableToChapter(newTableChapter).then(_res => {
            refreshContent();
            setTableSelectDialogOpen(false);
        });
    };

    const handleUpdateName = () => {
        let chapter =
            entity === "figure"
                ? (chapterContent.find(
                      cc => (cc as IFigureChapter).id === editNameSelection
                  ) as IFigureChapter)
                : (chapterContent.find(
                      cc => (cc as ITableChapter).id === editNameSelection
                  ) as ITableChapter);
        chapter.name = name;

        const updateFunction =
            entity === "figure"
                ? updateFigureChapter(chapter as IFigureChapter)
                : updateTableChapter(chapter as ITableChapter);

        updateFunction.then(res => {
            if (res.ok) {
                toast(`${entity} was successfully updated.`, true, 1500);
            } else {
                toast(`There was a problem with updating the ${entity}.`, false, 1500);
            }

            refreshContent();
            setEditNameDialogOpen(false);
        });
    };

    const renderParagraph = (p: IParagraph) => {
        return (
            <div
                className="rte-content-container action-bar-container"
                onBlur={() => saveExistingParagraph(p.id)}
            >
                <ReactQuill
                    key={p.id}
                    className="text-editor"
                    value={p.content}
                    modules={toolbarConfig.modules}
                    formats={toolbarConfig.formats}
                    onChange={val => {
                        let newChapterContent: IChapterContent[] = chapterContent.map(cc => {
                            return { ...cc };
                        });

                        let targetParagraph = newChapterContent.find(
                            np => np.id === p.id
                        ) as IParagraph;

                        if (targetParagraph) {
                            targetParagraph.content = val;
                            setChapterContent(newChapterContent);
                        }
                    }}
                />

                <div className="rte-actions-container action-bar-container-options">
                    <Icon
                        size="large"
                        name="delete"
                        onClick={() => {
                            setParagraphDeleteSelection(p.id);
                            setDeleteDialogOpen(true);
                        }}
                        bordered
                    />
                </div>
            </div>
        );
    };

    const renderEntity = (e: IFigureChapter | ITableChapter) => {
        const entity = typeof (e as IFigureChapter).figureId === "string" ? "figure" : "table";

        return (
            <div
                className="action-bar-container entity-container"
                key={
                    entity === "figure"
                        ? (e as IFigureChapter).figureId
                        : (e as ITableChapter).tableId
                }
            >
                <div className="entity-content-container">
                    {entity === "figure" ? (
                        <img src={`/api/figures/${(e as IFigureChapter).figureId}`} alt={e.name} />
                    ) : (
                        <span className="table-data-container">
                            <TableComponent
                                tableData={tablesData[(e as ITableChapter).tableId] ?? []}
                            />
                        </span>
                    )}
                    <p>{e.name}</p>
                </div>
                <div className="action-bar-container-options">
                    <Icon
                        size="large"
                        name="edit"
                        onClick={() => {
                            setName(e.name);
                            setEditNameSelection(e.id);
                            setEditNameDialogOpen(true);
                            setEntity(entity);
                        }}
                        bordered
                    />
                    <Icon
                        size="large"
                        name="delete"
                        onClick={() => {
                            setDeleteSelection(e.id);
                            setDeleteOpen(true);
                            setEntity(entity);
                        }}
                        bordered
                    />
                </div>
            </div>
        );
    };

    const renderChapterContent = (oc: IChapterContent) => {
        if ((oc as IFigureChapter).figureId) {
            return renderEntity(oc as IFigureChapter);
        } else if ((oc as ITableChapter).tableId) {
            return renderEntity(oc as ITableChapter);
        } else {
            return renderParagraph(oc as IParagraph);
        }
    };

    const unfocusElement = (newFocusedElement: string) => {
        if (contentFocused && newFocusedElement !== contentFocused) {
            (document.activeElement as HTMLElement).blur();
        }
    };

    const onDragEnd = (result: DropResult) => {
        if (
            result.source &&
            result.destination &&
            result.source.index !== result.destination.index
        ) {
            let newChapterContent: IChapterContent[] = chapterContent.map(cc => {
                return { ...cc };
            });

            let sourceItem = newChapterContent[result.source.index - 1];
            newChapterContent.splice(result.source.index - 1, 1);
            newChapterContent.splice(result.destination.index - 1, 0, sourceItem);

            newChapterContent.forEach((ncc, index) => {
                ncc.orderNumber = index + 1;
            });

            newChapterContent.sort((a, b) => a.orderNumber - b.orderNumber);

            let oldChapterContent = [...chapterContent];
            setChapterContent(newChapterContent);

            reorderContent(params.chapterId, result.source.index, result.destination.index).catch(
                _err => {
                    toast("Unable to complete reordering", false, 1000);
                    setChapterContent(oldChapterContent);
                }
            );
        }
    };

    return (
        <div className="chapter-container">
            <div>
                <div className="container-header">
                    <h1>Content</h1>
                    <ReportPublishControls
                        reportId={params.reportId}
                        isInternal={report.isInternal}
                    />
                </div>
                <Breadcrumbs />
                <div className="chapter-content-container">
                    <div className="chapter-content">
                        <label>Select a chapter</label>
                        <Select
                            className="chapter-select"
                            value={params.chapterId}
                            options={chapters.map(chapter => {
                                let prefix =
                                    chapter.sectionNumber != null
                                        ? chapter.sectionNumber + " "
                                        : "";

                                return {
                                    key: chapter.id,
                                    value: chapter.id,
                                    text: prefix + chapter.title,
                                };
                            })}
                            onChange={(_e, data) =>
                                history.push(
                                    report.isInternal
                                        ? `/figures-and-tables/internal-reports/${params.reportId}/chapters/${data.value}`
                                        : `/reports/overview/${params.reportId}/chapters/${data.value}`
                                )
                            }
                        />

                        <Loader active={loadingContent} inline="centered" />
                        <DragDropContext
                            onDragEnd={onDragEnd}
                            onDragStart={() => unfocusElement("")}
                        >
                            <Droppable droppableId="report-content-droppable">
                                {provided => (
                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                        {chapterContent.map((oc: IChapterContent, _index) => {
                                            return (
                                                <div
                                                    key={oc.id}
                                                    onClick={() => unfocusElement(oc.id)}
                                                    onFocus={() => setContentFocused(oc.id)}
                                                >
                                                    <Draggable
                                                        draggableId={oc.id}
                                                        index={oc.orderNumber}
                                                    >
                                                        {prov => (
                                                            <div
                                                                ref={prov.innerRef}
                                                                {...prov.draggableProps}
                                                                {...prov.dragHandleProps}
                                                            >
                                                                {renderChapterContent(oc)}
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                </div>
                                            );
                                        })}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <Menu vertical className="add-menu">
                            <Dropdown item text="Add" className="add-dropdown">
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={saveNewParagraph}>
                                        Paragraph
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setFigureSelectDialogOpen(true)}>
                                        Figure
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setTableSelectDialogOpen(true)}>
                                        Table
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Menu>

                        <Modal open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                            <Modal.Header>Deleting paragraph</Modal.Header>
                            <Modal.Content>
                                Are you sure you want to delete this paragraph. This action is not
                                reversable!
                            </Modal.Content>
                            <Modal.Actions>
                                <Button
                                    content="Cancel"
                                    onClick={() => setDeleteDialogOpen(false)}
                                />
                                <Button content="Delete" onClick={onDeleteParagraph} />
                            </Modal.Actions>
                        </Modal>

                        <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                            <Modal.Header>Remove {entity}</Modal.Header>
                            <Modal.Content>
                                Removing the {entity} only removes the {entity} from this chapter
                                and does not delete the {entity} entirely.
                            </Modal.Content>
                            <Modal.Actions>
                                <Button content="Cancel" onClick={() => setDeleteOpen(false)} />
                                <Button content="Delete" onClick={onDelete} />
                            </Modal.Actions>
                        </Modal>

                        <Modal
                            open={editNameDialogOpen}
                            onClose={() => setEditNameDialogOpen(false)}
                        >
                            <Modal.Header>Edit {entity} name</Modal.Header>
                            <Modal.Content>
                                <Form>
                                    <Form.Field>
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={e => setName(e.target.value)}
                                        />
                                    </Form.Field>
                                </Form>
                            </Modal.Content>
                            <Modal.Actions>
                                <Button
                                    content="Cancel"
                                    onClick={() => setEditNameDialogOpen(false)}
                                />
                                <Button content="Save" onClick={handleUpdateName} />
                            </Modal.Actions>
                        </Modal>
                    </div>
                    <div className="chapter-preview"></div>
                </div>
            </div>
            <div className="navigation-container">
                <Button
                    primary
                    content="Table of contents"
                    onClick={() =>
                        history.push(
                            report.isInternal
                                ? `/figures-and-tables/internal-reports/${params.reportId}/table-of-contents`
                                : `/reports/overview/${params.reportId}/table-of-contents`
                        )
                    }
                />
            </div>

            <FigureSelectionModal
                open={figureSelectDialogOpen}
                onClose={() => setFigureSelectDialogOpen(false)}
                onSelect={handleAddFigure}
            />
            <TableSelectionModal
                open={tableSelectDialogOpen}
                onClose={() => setTableSelectDialogOpen(false)}
                onSelect={handleAddTable}
            />
        </div>
    );
};
